import { FC, useState, useEffect, useMemo } from 'react';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { deepCopy, isArray, isNotEmpty } from '@lhb/func';
import { OTHER_TYPE_ID, OTHER_TYPE_INPUT_TEXT, OTHER_TYPE_NAME, setComValue } from '../config';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import { useDispatch, useSelector } from 'react-redux';
import { changeDynamicOtherFormNameArr } from '@/store/common';
import { Col, Row } from 'antd';
import styles from './index.module.less';
import { identificationStoreType } from '../customize';
import { customEstimateSalesKWK } from '@/common/components/business/ExpandStore/ts-config';
interface InputInfoI {
  visible:boolean;// 是否展示
  value:string;// input表单值
}

/**
 * @Description 下拉组件（支持单选、多选）
 */
const DropdownSelect: FC<{
  propertyItem: any;
  isMultiple?: boolean;
  optLabelFieldName?: string;
  optValueFieldName?: string;
  disabled?: boolean;
  required?: boolean;
  /** 单选、多选、输入框等值变更时的提示内容 */
  showHintStr?: any;
  form: any;
  salesFactorOfKWK?: any;
  /** 卡旺卡定制逻辑-预计销售额 */
  setSalesFactorOfKWK?: any;
}> = ({
  propertyItem,
  isMultiple = false,
  optLabelFieldName = 'name',
  optValueFieldName = 'id',
  disabled,
  required,
  showHintStr,
  form,
  salesFactorOfKWK,
  setSalesFactorOfKWK, // 卡旺卡定制逻辑-预计销售额
}) => {
  const dispatch = useDispatch();
  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName

  const [helpInfo, setHelpInfo] = useState<string>('');
  const [selectValue, setSelectValue] = useState<any>(null);// 保存radio值
  const [inputInfo, setInputInfo] = useState<InputInfoI>({
    visible: false,
    value: ''
  });
  const label = propertyItem.anotherName || propertyItem.name || '';
  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};
  const placeholder = restriction.placeholder ? restriction.placeholder : `请选择${label || ''}`;

  // 定制逻辑，门店类型需要支持搜索
  const showSearch = propertyItem?.identification === identificationStoreType;
  /**
   * 子表单格式需要支持 identification 传入数组形式，如 ['subForm', 0, 'radio']
   * 因为子表单内的数组形式暂时不考虑 customEstimateSalesKWK[identification]，如果需要，后续可以判断转为字符串
   */
  const identification = propertyItem.identification;
  /** 如果 identification 是数组格式，那么“其他”输入框的 prop 也是数组，将数组最后一项拼接 _otherTypeInputText，如 ['subForm', 0, 'radio' + OTHER_TYPE_INPUT_TEXT] */
  const otherTypeInputTextName = Array.isArray(identification)
    ? identification.slice(0, -1).concat(identification[identification.length - 1] + OTHER_TYPE_INPUT_TEXT)
    : `${identification}${OTHER_TYPE_INPUT_TEXT}`;

  const options = useMemo(() => {
    if (!propertyItem) return;
    const _options = Array.isArray(propertyItem.propertyConfigOptionVOList) ? deepCopy(propertyItem.propertyConfigOptionVOList) : [];

    // 根据restriction中有withOther判断是否存在“其他”选项，
    if (restriction?.withOther) {
      _options.push({
        name: OTHER_TYPE_NAME,
        id: OTHER_TYPE_ID,
      });
      dispatch(changeDynamicOtherFormNameArr(Array.from(new Set([...dynamicOtherFormNameArr, otherTypeInputTextName]))));

    }
    return _options;
  }, []);

  const rules = [
    { required, message: placeholder },
  ];
  // 回显
  useEffect(() => {
    // 使用更通用的方式获取选中项和“其他”
    const selectedId = form.getFieldValue(identification);
    const otherTypeInputText = form.getFieldValue(otherTypeInputTextName);
    const option = options.find((item) => item.id === selectedId);
    if (isNotEmpty(selectedId)) {
      changeHandle(selectedId, option);
      if (selectedId === OTHER_TYPE_ID) {
        form.setFieldValue(otherTypeInputTextName, otherTypeInputText);
      }
    }
  }, [propertyItem, options]);

  const changeHandle = (value: number, option?: any) => {
    setSelectValue(value);
    // 注意不要在编辑回显的时候传入option
    if (salesFactorOfKWK && option && option.name && customEstimateSalesKWK[propertyItem?.identification]) {
      const targetFields = customEstimateSalesKWK[identification]; // 该字段对应的所有影响因子
      const targetOption = targetFields?.find((item) => item.name === option.name); // 选项值对应的影响因子
      targetOption && setSalesFactorOfKWK && setSalesFactorOfKWK((state) => ({ ...state, [identification]: targetOption.value }));
    }
    // 是否选中了“其他”
    if (value === OTHER_TYPE_ID) {
      setInputInfo({
        visible: true,
        value: ''
      });
    } else {
      setInputInfo({
        visible: false,
        value: ''
      });
    }

    const { restriction } = propertyItem || {};
    setHelpInfo(''); // 清空
    // 取消选中或者没有配置时不继续执行
    if (!restriction) return;
    const parsedRestriction = JSON.parse(restriction);
    const { redMark } = parsedRestriction || {};
    if (!(isArray(redMark) && value)) return; // 注意这里预期id不会为0
    const targetConfig = redMark.find((item: any) => item.optionId === value);
    if (targetConfig) {
      const { isRedMark, isItemTip, itemTip, isPageTip } = targetConfig;
      isRedMark && isItemTip && itemTip && (setHelpInfo(itemTip));
      isPageTip && showHintStr && (showHintStr());
    }
  };

  const handleOnClear = () => {
    if (salesFactorOfKWK && customEstimateSalesKWK[propertyItem?.identification]) {
      const _copyValue = deepCopy(salesFactorOfKWK);
      Reflect.deleteProperty(_copyValue, identification);
      setSalesFactorOfKWK && setSalesFactorOfKWK(_copyValue);
    }
  };

  useEffect(() => {
    if (inputInfo.visible && inputInfo.value) {
      propertyItem[OTHER_TYPE_INPUT_TEXT] = inputInfo.value;
      setComValue(propertyItem, selectValue);
    }
  }, [inputInfo.value, selectValue]);

  return (
    <Row>
      <Col span={inputInfo.visible ? 12 : 24}
        style={{
          paddingLeft: 0,
          paddingRight: 0
        }}
      >
        <V2FormSelect
          label={label}
          name={identification}
          placeholder={placeholder}
          options={options}
          disabled={disabled}
          required={required}
          config={{
            getPopupContainer: (node) => node.parentNode,
            showSearch: showSearch, // UI认为右侧不应展示搜索icon，应展示下拉箭头，门店类型特殊需求支持搜索
            mode: isMultiple ? 'multiple' : undefined,
            fieldNames: { label: optLabelFieldName, value: optValueFieldName },
            filterOption: (input, option) => (
              ((option?.[optLabelFieldName] ?? '') as any).toLowerCase().includes(input.toLowerCase())),
            onClear: handleOnClear
          }}
          rules={rules}
          onChange={changeHandle}
          formItemConfig={{
            help: helpInfo ? <div className='c-f23'>{helpInfo}</div> : null,
          }}
        />
      </Col>

      <Col span={inputInfo.visible ? 12 : 0}
        className={styles.dropdownSelectOther}
        style={{
          paddingLeft: 0,
          paddingRight: 0
        }}
      >
        {inputInfo.visible ? <V2FormInput
          name={otherTypeInputTextName}
          maxLength={50}
          onChange={(e) => setInputInfo((state) => ({
            ...state,
            value: e.target.value
          }))}
          placeholder='请输入其他选项值'
          rules={[{ required: true, message: '请输入其他选项值' }]}
        /> : <></>}
      </Col>

    </Row>
  );
};

export default DropdownSelect;
